import { useCallback, useState } from 'react';
import { storageService } from '../services/storage';

export interface UseLocalStorageProps<T> {
  key: string;
  defaultValue?: T;
  parse?: boolean;
}
export interface UseLocalStorageResponse<T> {
  state: T;
  set: (data: T) => void;
  merge: (data: Partial<T> | ((data: T) => T)) => void;
  clear: () => void;
}

const get = <T>({ key, defaultValue, parse }: UseLocalStorageProps<T>) =>
  parse
    ? ({
        ...defaultValue,
        ...storageService.getParsed<T>(key, defaultValue),
      } as T)
    : ((storageService.get(key) as T | undefined) ?? (defaultValue as T));

export const useLocalStorage = <T>({
  key,
  defaultValue,
  parse = false,
}: UseLocalStorageProps<T>) => {
  type Def = UseLocalStorageResponse<T>;
  const [state, setState] = useState<T>(() => get({ key, defaultValue, parse }));
  const set = useCallback<Def['set']>(
    (value) => {
      storageService.set(key, value);
      setState(value);
    },
    [key],
  );
  const clear = useCallback<Def['clear']>(() => {
    storageService.remove(key);
    setState(defaultValue as T);
  }, [key, defaultValue]);
  const merge = useCallback<Def['merge']>(
    (value) => {
      const latest = get({ key, defaultValue, parse });
      setState(() => {
        const newVal = typeof value === 'function' ? value(latest) : ({ ...latest, ...value } as T);
        storageService.set(key, newVal);
        return newVal;
      });
    },
    [defaultValue, key, parse],
  );
  return { state, set, merge, clear };
};
